/**
 * 404 Not Found Page Component
 *
 * A friendly and helpful 404 error page that provides users with:
 * - Clear messaging about what happened
 * - Multiple ways to navigate back to useful content
 * - Visual design that matches the site's branding
 */
import { Home, Search, HelpCircle, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";

export default function NotFound() {
  const { t } = useTranslation();

  // Popular pages to suggest
  const popularPages = [
    { href: "/explain", icon: "📚", label: "설명하기", description: "AI가 개념 설명을 도와드립니다" },
    { href: "/chat", icon: "💬", label: "대화하기", description: "AI와 1:1 학습 대화" },
    { href: "/pricing", icon: "💎", label: "요금제", description: "프리미엄 플랜 알아보기" },
    { href: "/contact", icon: "✉️", label: "문의하기", description: "도움이 필요하신가요?" },
  ];

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="mx-auto max-w-4xl text-center">
        {/* Error illustration */}
        <div className="mb-8">
          <div className="mx-auto mb-4 flex h-32 w-32 items-center justify-center rounded-full bg-primary text-6xl text-primary-foreground shadow-lg">
            <Search className="h-16 w-16" />
          </div>
          <h1 className="mb-4 text-6xl font-bold text-primary md:text-8xl">
            404
          </h1>
          <h2 className="text-3xl font-semibold text-foreground md:text-4xl">
            페이지를 찾을 수 없습니다
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            요청하신 페이지가 존재하지 않거나 이동되었을 수 있습니다.
          </p>
        </div>

        {/* Action buttons */}
        <div className="mb-12 flex flex-wrap items-center justify-center gap-4">
          <Button size="lg" asChild>
            <Link to="/">
              <Home className="mr-2 h-5 w-5" />
              홈으로 가기
            </Link>
          </Button>
          <Button size="lg" variant="outline" onClick={() => window.history.back()}>
            <ArrowLeft className="mr-2 h-5 w-5" />
            이전 페이지
          </Button>
        </div>

        {/* Popular pages */}
        <div className="mx-auto max-w-3xl">
          <h3 className="mb-6 text-xl font-semibold text-foreground">
            이런 페이지는 어떠세요?
          </h3>
          <div className="grid gap-4 md:grid-cols-2">
            {popularPages.map((page) => (
              <Link key={page.href} to={page.href} className="group">
                <Card className="h-full transition-all hover:shadow-lg hover:-translate-y-1">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-left">
                      <span className="text-2xl">{page.icon}</span>
                      <span className="group-hover:text-primary">{page.label}</span>
                    </CardTitle>
                    <CardDescription className="text-left">
                      {page.description}
                    </CardDescription>
                  </CardHeader>
                </Card>
              </Link>
            ))}
          </div>
        </div>

        {/* Help section */}
        <div className="mt-12">
          <Card className="bg-accent border-border">
            <CardContent className="pt-6">
              <div className="flex items-center justify-center gap-3 text-foreground">
                <HelpCircle className="h-6 w-6" />
                <p>
                  문제가 계속되나요?{" "}
                  <Link
                    to="/contact"
                    className="font-semibold underline hover:text-primary"
                  >
                    고객지원팀에 문의하세요
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
